import React from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useLocation } from "react-router-dom";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../Layout.tsx";
import Title from "../Title.tsx";
import { readPositions, updatePositionsOrder } from "../../services/position.service.ts";
import { notification } from "../../utils/Notifications.tsx";

const normalizeSearchText = (value: string) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const EditOrderPosition = ({ closeFn, refreshFn, setLoading }) => {
  const location = useLocation();
  const { positionsList } = location.state || { positionsList: [] };

  const [data, setData] = React.useState<any[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<number[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = React.useState(-1);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    const sortBySavedOrder = (list: any[]) =>
      [...(Array.isArray(list) ? list : [])].sort((a: any, b: any) => {
        const orderA = Number(a?.order);
        const orderB = Number(b?.order);
        const safeA = Number.isFinite(orderA) ? orderA : Number.MAX_SAFE_INTEGER;
        const safeB = Number.isFinite(orderB) ? orderB : Number.MAX_SAFE_INTEGER;
        if (safeA !== safeB) return safeA - safeB;
        const nameA = String(a?.name || "");
        const nameB = String(b?.name || "");
        return nameA.localeCompare(nameB, "es");
      });

    const hydrateData = (list: any[]) => {
      const sorted = sortBySavedOrder(list);
      setData(
        sorted.map((item: any, index: number) => ({
          ...item,
          originalIndex: index,
        })),
      );
    };

    const loadPositions = async () => {
      try {
        const latest = await readPositions();
        hydrateData(latest || []);
      } catch (error) {
        hydrateData(positionsList || []);
      }
    };

    loadPositions();
  }, [positionsList]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(data) as any[];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setData(items);

    const positionsOrder = items.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    try {
      await updatePositionsOrder(positionsOrder);
      notification("Éxito", "Orden de puestos guardado correctamente", "success");
    } catch (error) {
      notification("Error", "No se pudo guardar el orden de puestos", "danger");
      console.error("Error updating order:", error);
    }
  };

  const handleSearch = (e) => {
    const termRaw = e.target.value || "";
    const term = normalizeSearchText(termRaw);
    setSearchTerm(termRaw);

    const exactMatches = data.reduce((acc: number[], puesto: any, index: number) => {
      const puestoName = normalizeSearchText(puesto.name || "");
      if (puestoName === term) {
        acc.push(index);
      }
      return acc;
    }, []);

    const partialMatches = data.reduce((acc: number[], puesto: any, index: number) => {
      const puestoName = normalizeSearchText(puesto.name || "");
      if (
        puestoName.includes(term) &&
        puestoName !== term
      ) {
        acc.push(index);
      }
      return acc;
    }, []);

    const results = [...exactMatches, ...partialMatches];
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);

    if (results.length > 0) {
      scrollToItem(results[0]);
    }
  };

  const scrollToItem = (index) => {
    const itemElements = listRef.current?.querySelectorAll(".draggable-item");
    if (!itemElements) return;
    itemElements.forEach((item) =>
      item.classList.remove("highlight", "active", "selected")
    );

    const itemElement = itemElements[index];
    if (itemElement) {
      itemElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      itemElement.classList.add("highlight", "active", "selected");
      setSelectedIndex(index);
    }
  };

  const handleNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    setTimeout(() => scrollToItem(searchResults[nextIndex]), 100);
    setSelectedIndex(searchResults[nextIndex]);
  };

  const handlePrevResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex =
      (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
    setTimeout(() => scrollToItem(searchResults[prevIndex]), 100);
    setSelectedIndex(searchResults[prevIndex]);
  };

  const handleSelectSource = (index) => {
    setSelectedIndex(index);
  };

  const getArrow = (currentIndex, originalIndex) => {
    if (currentIndex < originalIndex) {
      return <span style={{ color: "green", marginLeft: "8px" }}>▲</span>;
    } else if (currentIndex > originalIndex) {
      return <span style={{ color: "red", marginLeft: "8px" }}>▼</span>;
    }
    return null;
  };

  useAppSettings();

  return (
    <Layout>
      <Title
        baseTitle="Configuración"
        basePath="/configuracion"
        activeTitle={`Orden de puestos`}
        title={`Orden de puestos`}
      />
      <div className="card border-0 m-4 rounded-3 shadow-sm">
        <div className="tab-content p-4">
          <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
            <div className="position-relative flex-grow-1 min-w-0">
              <i
                className="fa-solid fa-magnifying-glass"
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  fontSize: "13px",
                }}
              />
              <input
                type="text"
                className="form-control"
                placeholder="Buscar puesto"
                value={searchTerm}
                onChange={handleSearch}
                style={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  height: "42px",
                  paddingLeft: "34px",
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#F9FAFB",
                }}
              />
            </div>

            {searchResults.length > 0 && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handlePrevResult}
                  style={{
                    borderRadius: "10px",
                    minWidth: "98px",
                    fontWeight: 500,
                  }}
                >
                  Anterior
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleNextResult}
                  style={{
                    borderRadius: "10px",
                    minWidth: "98px",
                    fontWeight: 500,
                  }}
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>

          <div
            className="d-flex justify-content-between align-items-center mb-3 px-1"
            style={{ color: "#6B7280", fontSize: "12px" }}
          >
            <span>
              Total de puestos: <strong style={{ color: "#374151" }}>{data.length}</strong>
            </span>
            {searchResults.length > 0 && (
              <span>
                Resultado:{" "}
                <strong style={{ color: "#111827" }}>
                  {currentResultIndex + 1}
                </strong>{" "}
                de <strong style={{ color: "#111827" }}>{searchResults.length}</strong>
              </span>
            )}
          </div>

          <div
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "12px",
              backgroundColor: "#FCFCFD",
            }}
          >
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="positions">
                {(droppableProvider) => (
                  <div
                    {...droppableProvider.droppableProps}
                    ref={(el) => {
                      droppableProvider.innerRef(el);
                      listRef.current = el;
                    }}
                    style={{ overflowY: "auto", maxHeight: "470px" }}
                    className="px-1"
                  >
                    {data.map((puesto, index) => (
                      <Draggable
                        index={index}
                        key={puesto.id}
                        draggableId={puesto.id.toString()}
                      >
                        {(draggableProvider) => (
                          <div
                            ref={draggableProvider.innerRef}
                            {...draggableProvider.draggableProps}
                            {...draggableProvider.dragHandleProps}
                            className={`draggable-item ${selectedIndex === index ? "selected" : ""}`}
                            onClick={() => handleSelectSource(index)}
                          >
                            <div
                              className="position-card mb-2"
                              style={{
                                borderRadius: "10px",
                                border: "1px solid #E5E7EB",
                                backgroundColor: "#FFFFFF",
                                padding: "11px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
                              }}
                            >
                              <div
                                style={{
                                  minWidth: "26px",
                                  height: "26px",
                                  borderRadius: "7px",
                                  backgroundColor: "#EEF2FF",
                                  color: "#4338CA",
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {index + 1}
                              </div>

                              <i
                                className="fa-solid fa-grip-vertical"
                                style={{ color: "#9CA3AF", fontSize: "12px" }}
                              />

                              <span
                                className={`fw-bold ${
                                  index === searchResults[currentResultIndex]
                                    ? "highlight"
                                    : ""
                                }`}
                                style={{ color: "#111827", fontSize: "14px" }}
                              >
                                {puesto.name}
                              </span>
                              <div className="ms-auto">{getArrow(index, puesto.originalIndex)}</div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvider.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

        </div>
      </div>

      <style>
        {`
  .draggable-item.highlight {
    animation: highlight 2s ease-in-out;
    background-color: #ffd700 !important;
  }

  .draggable-item.selected {
    font-weight: bold;
  }

  .draggable-item.selected .position-card {
    border-color: #818cf8 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2), 0 1px 2px rgba(16, 24, 40, 0.08) !important;
  }

  .position-card {
    transition: all 0.16s ease;
  }

  .draggable-item:hover .position-card {
    border-color: #c7d2fe;
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }

  @keyframes highlight {
    0% { background-color: #ffd700; }
    100% { background-color: transparent; }
  }
`}
      </style>
    </Layout>
  );
};

export default EditOrderPosition;
